import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { FC, ReactNode, useId, useState } from 'react'
import { useLocaleState, useTranslate } from 'react-admin'

import type { EventRow, ScaleRecord } from '../../data/source'
import { formatDateTime } from '../../domain/dateTime'
import { DetailList } from '../atoms/DetailList'
import { hasStayDetails, StayDetails } from './StayDetails'

const Group: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Box component="section" aria-label={label} display="flex" flexDirection="column" gap={1}>
    <Typography variant="subtitle2" component="h3" fontWeight="bold">
      {label}
    </Typography>
    {children}
  </Box>
)

/** What EQ recorded on a MARPOL/BOMBEO event, as one line; empty when nothing yet. */
const useEqSummary = (): ((event: EventRow) => string) => {
  const translate = useTranslate()
  const [locale] = useLocaleState()
  return event => {
    if (event.type === 'MARPOL') {
      return event.marpolNoticeAt
        ? `${translate('resources.scale_events.fields.marpolNoticeAt')}: ${formatDateTime(event.marpolNoticeAt)}`
        : ''
    }
    return [
      event.connectionPoint,
      event.operationKind && translate(`enums.OperationKind.${event.operationKind}`),
      event.productClass && translate(`enums.ProductClass.${event.productClass}`),
      event.product,
      event.quantityT !== null && translate('pages.scales.stay.tonnes', { quantity: event.quantityT.toLocaleString(locale) }),
    ]
      .filter(Boolean)
      .join(' · ')
  }
}

/**
 * ℹ️ +Info: what the scale page leaves out. The scale's SOSTRAT details, then,
 * per event (in the page's order), each berth stay's SOSTRAT details and what EQ
 * recorded on each BOMBEO, labelled with the stay's muelle or the event's type
 * and start so each value traces back to its event.
 */
export const ScaleInfoDialog: FC<{ scale: ScaleRecord; events: EventRow[] }> = ({ scale, events }) => {
  const translate = useTranslate()
  const [locale] = useLocaleState()
  const [open, setOpen] = useState(false)
  const titleId = useId()
  const eqSummary = useEqSummary()
  const close = () => {
    setOpen(false)
  }

  const number = (value: number | null, unit: string) => (value === null ? null : `${value.toLocaleString(locale)}${unit}`)
  const time = (value: string | null) => (value === null ? null : formatDateTime(value))
  const scaleDetails = [
    { key: 'imo', value: scale.imo },
    { key: 'eslora', value: number(scale.eslora, ' m') },
    { key: 'bcm', value: number(scale.bcm, ' m') },
    { key: 'consignataria', value: scale.consignataria },
    { key: 'firstEta', value: time(scale.firstEta) },
    { key: 'firstEtd', value: time(scale.firstEtd) },
    { key: 'terminals', value: scale.terminals.length > 0 ? scale.terminals.join(', ') : null },
  ].map(detail => ({ ...detail, label: translate(`resources.scales.fields.${detail.key}`) }))

  const eventGroups = events.flatMap(event => {
    const start = formatDateTime(event.startTime)
    if (event.type === 'PLACE') {
      if (!hasStayDetails(event)) return []
      return [
        <Group key={event.id} label={`${event.muelle} · ${start}`}>
          <StayDetails stay={event} />
        </Group>,
      ]
    }
    const summary = eqSummary(event)
    if (!summary) return []
    return [
      <Group key={event.id} label={`${translate(`enums.ScaleEventType.${event.type}`)} · ${start}`}>
        <Typography variant="body2" data-testid="eq-summary">
          {summary}
        </Typography>
      </Group>,
    ]
  })

  return (
    <>
      <Button
        variant="text"
        color="inherit"
        startIcon={<InfoOutlined />}
        onClick={() => {
          setOpen(true)
        }}
        sx={{ textTransform: 'none' }}
      >
        {translate('pages.scales.info.button')}
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="md" aria-labelledby={titleId}>
        <DialogTitle id={titleId}>{translate('pages.scales.info.title', { scaleId: scale.scaleId, year: String(scale.year) })}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Group label={translate('resources.scales.fields.scaleId')}>
            <DetailList details={scaleDetails} />
          </Group>
          {eventGroups}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{translate('ra.action.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
